import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import kotlin.coroutines.cancellation.CancellationException
import models.GameRepository
import models.JoinGameResult
import models.LimitedTimeGameModes
import models.Player
import models.PublicGameSummary
import models.RoundModifier
import models.Setting

import util.networkAvailableFlow
import kotlinx.coroutines.flow.collectLatest

class BankoStartScreenUseCase(
    private val gameRepository: GameRepository,
    private val scope: CoroutineScope,
    private val playerPreferences: PlayerPreferences,
    // Injectable so tests can supply a deterministic flow instead of the real connectivity monitor.
    private val networkFlow: Flow<Boolean> = networkAvailableFlow()
) {
    private val userNameFlow = MutableStateFlow("")
    private val gameCodeFlow = MutableStateFlow("")
    private val isLoadingFlow = MutableStateFlow(false)
    private val errorMessageFlow = MutableEventStateFlow<String>()
    private val joinedGameIsValidFlow = MutableEventStateFlow<Boolean>()
    private val joinedGameCodeFlow = MutableStateFlow("")
    private val isOfflineModeFlow = MutableStateFlow(false) // Default to online if possible
    private val isOfflineToggleLockedFlow = MutableStateFlow(false)
    private var userToggledOffline = false

    // True while the game code in the field came from an invite deep link, so a join — whether
    // auto-fired or via the Join button after typing a name — is attributed to the invite. A manual
    // edit of the code clears it.
    private var joinCameFromInvite = false

    // False while a game is open over the start screen. An invite link that arrives then stays
    // pending until the player is back here, instead of joining a second game behind the first.
    private val screenVisibleFlow = MutableStateFlow(true)

    // Set when this screen hands off to a game, so the form is tidied when the player comes back.
    private var openedGame = false

    // True while the practice fallback name sits in the name field only so navigation can read it.
    private var nameFieldHoldsPracticeFallback = false
    private val selectedDiceSkinFlow = MutableStateFlow(
        playerPreferences.getSelectedDiceSkin() ?: DiceSkin.DEFAULT.id
    )
    private val selectedDiceHatFlow = MutableStateFlow(
        playerPreferences.getSelectedDiceHat() ?: DiceHat.NONE.id
    )
    // One-time practice-round offer: only for players who have never been in a game and have no
    // saved name, so existing players updating the app never see it.
    private val practiceOfferVisibleFlow = MutableStateFlow(
        !playerPreferences.getHasCompletedFirstGame() &&
                playerPreferences.getPlayerName().isNullOrBlank()
    )
    private val publicGamesFlow = MutableStateFlow<List<PublicGameSummary>>(emptyList())
    private val publicGamesLoadingFlow = MutableStateFlow(false)
    private val publicGamesErrorFlow = MutableStateFlow<String?>(null)
    private val publicJoinErrorFlow = MutableStateFlow<String?>(null)
    private val publicGameBrowserVisibleFlow = MutableStateFlow(false)
    private var publicGamesJob: Job? = null
    private var publicGamesResultLogged = false

    init {
        // Restore the saved player name so returning players don't retype it (and so invite links
        // can auto-join). Done synchronously before the deep-link collector so the name is in place.
        playerPreferences.getPlayerName()?.takeIf { it.isNotBlank() }?.let {
            userNameFlow.value = it
        }

        // Apply incoming invite deep links once the start screen is showing: force online mode and
        // pre-fill the code. If we already know the player's name, auto-join straight away;
        // otherwise just pre-fill so they can enter one.
        scope.launch {
            InviteLinks.pendingInviteCode.combine(screenVisibleFlow) { code, visible ->
                code.takeIf { visible }
            }.collect { code ->
                if (!code.isNullOrBlank()) {
                    InviteLinks.consumeSuggestedPlayerName()?.let { suggestedName ->
                        val validation = PlayerNameValidator.validate(suggestedName)
                        if (validation is PlayerNameValidator.Result.Valid) {
                            userNameFlow.value = validation.name
                        }
                    }
                    if (isOfflineModeFlow.value && !isOfflineToggleLockedFlow.value) {
                        isOfflineModeFlow.value = false
                        userToggledOffline = false
                        gameRepository.setUseLocalDataSource(false)
                    }
                    gameCodeFlow.value = code
                    joinCameFromInvite = true
                    InviteLinks.consume()
                    if (userNameFlow.value.isNotBlank()) {
                        performJoin(code.uppercase(), Analytics.JoinSource.InviteLink)
                    }
                }
            }
        }

        // Live network monitoring
        scope.launch {
            networkFlow.collectLatest { networkAvailable ->
                if (!networkAvailable) {
                    isOfflineModeFlow.value = true
                    isOfflineToggleLockedFlow.value = true
                    gameRepository.setUseLocalDataSource(true)
                    userToggledOffline = false // Reset so auto-switch works when network returns
                    publicGamesJob?.cancel()
                    publicGameBrowserVisibleFlow.value = false
                } else {
                    isOfflineToggleLockedFlow.value = false
                    // Only auto-switch to online if user didn't manually toggle offline
                    if (!userToggledOffline) {
                        isOfflineModeFlow.value = false
                        gameRepository.setUseLocalDataSource(false)
                    }
                }
            }
        }
    }

    operator fun invoke(): BankoStartScreenUiState {
        return BankoStartScreenUiState(
            userNameFlow = userNameFlow.asStateFlow(),
            gameCodeFlow = gameCodeFlow.asStateFlow(),
            isLoadingFlow = isLoadingFlow.asStateFlow(),
            errorMessageFlow = errorMessageFlow,
            joinedGameIsValidFlow = joinedGameIsValidFlow,
            joinedGameCodeFlow = joinedGameCodeFlow.asStateFlow(),
            isOfflineModeFlow = isOfflineModeFlow.asStateFlow(),
            // Add locked state to UI state
            isOfflineToggleLockedFlow = isOfflineToggleLockedFlow.asStateFlow(),
            selectedDiceSkinFlow = selectedDiceSkinFlow.asStateFlow(),
            selectedDiceHatFlow = selectedDiceHatFlow.asStateFlow(),
            publicGamesFlow = publicGamesFlow.asStateFlow(),
            publicGamesLoadingFlow = publicGamesLoadingFlow.asStateFlow(),
            publicGamesErrorFlow = publicGamesErrorFlow.asStateFlow(),
            publicJoinErrorFlow = publicJoinErrorFlow.asStateFlow(),
            publicGameBrowserVisibleFlow = publicGameBrowserVisibleFlow.asStateFlow(),
            practiceOfferVisibleFlow = practiceOfferVisibleFlow.asStateFlow(),
            onUpdateUserName = { userNameFlow.value = it },
            onUpdateGameCode = {
                if (it != gameCodeFlow.value) joinCameFromInvite = false
                gameCodeFlow.value = it
            },
            onToggleOfflineMode = { isOffline ->
                // Only allow toggle if not locked
                if (!isOfflineToggleLockedFlow.value) {
                    isOfflineModeFlow.value = isOffline
                    gameRepository.setUseLocalDataSource(isOffline)
                    userToggledOffline = isOffline
                    if (isOffline) {
                        gameCodeFlow.value = ""
                    }
                }
            },
            onCreateGame = {
                scope.launch {
                    isLoadingFlow.value = true
                    try {
                        val userName = userNameFlow.value
                        val validation = PlayerNameValidator.validate(userName)
                        if (validation is PlayerNameValidator.Result.Invalid) {
                            errorMessageFlow.send(validation.reason)
                            return@launch
                        }
                        val validatedName = (validation as PlayerNameValidator.Result.Valid).name
                        val offlineMode = isOfflineModeFlow.value
                        Analytics.lobbyCreateAttempt(offline = offlineMode)
                        gameRepository.setUseLocalDataSource(offlineMode)
                        val purchaseState = RemoveAdsPurchases.uiState.value
                        if (
                            !offlineMode &&
                            purchaseState.isPurchasesSupported &&
                            purchaseState.isConfigured &&
                            !purchaseState.entitlementsResolved
                        ) {
                            Analytics.lobbyCreateFailed(
                                offline = false,
                                reason = "purchases_loading",
                            )
                            errorMessageFlow.send("Purchases are still loading. Try again in a moment.")
                            return@launch
                        }
                        val gameCode = if (offlineMode) {
                            GameRepository.LOCAL_GAME_CODE
                        } else {
                            unlockCreatorDiceForCurrentFirebaseUser()
                            generateValidGameCode()
                        }
                        // A rewarded-ad Plus Pass covers this game's host perks when the player
                        // doesn't own Plus, and a pending dice trial overrides the cosmetic
                        // resolution. Both are spent only after the game is actually created, so
                        // a failed create doesn't burn them.
                        val ownsHostPerks = hostPerksActiveForCreatedGame(purchaseState)
                        val usePlusPass = !ownsHostPerks && PlusPass.pending.value
                        val trialSkinId = DiceTrial.pendingSkinId.value
                        val trialHatId = DiceTrial.pendingHatId.value
                        val createdGameCode = gameRepository.createGame(
                            gameCode = gameCode,
                            name = validatedName,
                            hostPerksActive = ownsHostPerks || usePlusPass,
                            diceSkin = trialSkinId ?: selectedUnlockedDiceSkinId(),
                            diceHat = trialHatId ?: selectedUnlockedDiceHatId(),
                        )
                        if (usePlusPass && PlusPass.consume()) {
                            Analytics.plusPassUsed()
                        }
                        consumeDiceTrials(trialSkinId, trialHatId)
                        playerPreferences.setPlayerName(validatedName)
                        markFirstGameExperienced()
                        joinedGameCodeFlow.value = createdGameCode
                        openedGame = true
                        joinedGameIsValidFlow.send(true)
                        Analytics.lobbyCreated(
                            offline = offlineMode,
                            usedPlusPass = usePlusPass,
                        )
                        Analytics.hostedGame(offline = offlineMode)
                    } catch (e: CancellationException) {
                        throw e
                    } catch (e: Exception) {
                        // The player sees a generic message; keep the real cause in the logs.
                        println("[BankoCreate] create game failed: $e")
                        Analytics.lobbyCreateFailed(
                            offline = isOfflineModeFlow.value,
                            reason = "backend_or_network",
                        )
                        errorMessageFlow.send("Couldn't create game. Check your connection and try again.")
                    } finally {
                        isLoadingFlow.value = false
                    }
                }
            },
            onJoinGame = {
                scope.launch {
                    val via = if (joinCameFromInvite) {
                        Analytics.JoinSource.InviteLink
                    } else {
                        Analytics.JoinSource.Manual
                    }
                    performJoin(gameCodeFlow.value.uppercase(), via)
                }
            },
            onQuickPlay = {
                scope.launch {
                    isLoadingFlow.value = true
                    try {
                        // Use the typed name when it's valid, otherwise fall back to a friendly
                        // default — the practice round should never block on the form.
                        val typedName = PlayerNameValidator.validate(userNameFlow.value)
                        val playerName = (typedName as? PlayerNameValidator.Result.Valid)?.name
                            ?: PRACTICE_FALLBACK_NAME
                        gameRepository.createGame(
                            gameCode = GameRepository.LOCAL_GAME_CODE,
                            name = playerName,
                            hostPerksActive = hostPerksActiveForCreatedGame(RemoveAdsPurchases.uiState.value),
                            diceSkin = selectedUnlockedDiceSkinId(),
                            diceHat = selectedUnlockedDiceHatId(),
                        )
                        // Practice teaches the base rules. Limited-time event modes default ON
                        // during their windows (e.g. Fireworks scoring safe 7s as 76), which
                        // would teach a first-timer the wrong game — force them off here.
                        gameRepository.domainGameFlow(GameRepository.LOCAL_GAME_CODE).first()
                            ?.let { practiceGame ->
                                practiceGame.settings
                                    .filter {
                                        it.name in LimitedTimeGameModes.settingIds &&
                                                it.value == "true"
                                    }
                                    .forEach { setting ->
                                        gameRepository.updateSetting(
                                            GameRepository.LOCAL_GAME_CODE,
                                            practiceGame,
                                            Setting(setting.name, "false"),
                                        )
                                    }
                            }
                        PRACTICE_BOT_NAMES.forEach { botName ->
                            gameRepository.addPlayer(
                                Player(name = botName, points = 0, hostCreated = true, isBot = true),
                                GameRepository.LOCAL_GAME_CODE,
                            )
                        }
                        // A practice round teaches the roll/bank/bust rhythm — it shouldn't run a
                        // full game's length. Short enough that new players actually finish (which
                        // is also what earns the Training Wheels dice).
                        gameRepository.updateEndRoundNum(
                            GameRepository.LOCAL_GAME_CODE,
                            PRACTICE_ROUND_COUNT,
                        )
                        gameRepository.startGame(
                            gameCode = GameRepository.LOCAL_GAME_CODE,
                            activeOrderedPlayerNames = listOf(playerName) + PRACTICE_BOT_NAMES,
                            firstRoundModifier = RoundModifier.NONE,
                        )
                        if (typedName is PlayerNameValidator.Result.Valid) {
                            playerPreferences.setPlayerName(typedName.name)
                        }
                        // Navigation reads the player name from the name field, so it must hold
                        // the resolved name (fallback included) before the ready signal fires.
                        userNameFlow.value = playerName
                        nameFieldHoldsPracticeFallback = typedName !is PlayerNameValidator.Result.Valid
                        // Finishing this game (not just any LOCAL game) earns the Training Wheels dice.
                        PracticeGameSession.markLaunched()
                        markFirstGameExperienced()
                        joinedGameCodeFlow.value = GameRepository.LOCAL_GAME_CODE
                        openedGame = true
                        joinedGameIsValidFlow.send(true)
                        Analytics.practiceOffer(accepted = true)
                        Analytics.gameStarted(
                            playerCount = 1 + PRACTICE_BOT_NAMES.size,
                            botCount = PRACTICE_BOT_NAMES.size,
                            ginko = false,
                            offline = true,
                        )
                    } catch (e: CancellationException) {
                        throw e
                    } catch (_: Exception) {
                        errorMessageFlow.send("Couldn't start the practice game. Try again.")
                    } finally {
                        isLoadingFlow.value = false
                    }
                }
            },
            onDismissPracticeOffer = {
                Analytics.practiceOffer(accepted = false)
                markFirstGameExperienced()
            },
            onShowPublicGames = {
                startPublicGamesBrowser()
            },
            onRefreshPublicGames = {
                startPublicGamesBrowser()
            },
            onHidePublicGames = {
                publicGameBrowserVisibleFlow.value = false
                publicGamesJob?.cancel()
                publicGamesJob = null
                publicGamesLoadingFlow.value = false
                publicJoinErrorFlow.value = null
            },
            onJoinPublicGame = { code ->
                scope.launch { performJoin(code, Analytics.JoinSource.PublicGames) }
            },
            onDiceSkinSelected = { id ->
                playerPreferences.setSelectedDiceSkin(id)
                selectedDiceSkinFlow.value = id
            },
            onDiceHatSelected = { id ->
                playerPreferences.setSelectedDiceHat(id)
                selectedDiceHatFlow.value = id
            },
            onScreenVisibilityChanged = { visible ->
                if (visible && openedGame) {
                    openedGame = false
                    // Back from a game. This use case outlives it (Compose scopes it to the Home
                    // entry, iOS keeps one for the app), so clear what that game left in the form:
                    // the joined code, and the practice fallback name, which was never saved.
                    gameCodeFlow.value = ""
                    joinCameFromInvite = false
                    if (nameFieldHoldsPracticeFallback) {
                        userNameFlow.value = playerPreferences.getPlayerName().orEmpty()
                    }
                    nameFieldHoldsPracticeFallback = false
                }
                screenVisibleFlow.value = visible
            },
        )
    }

    /** Spends the dice trials that were applied to a successfully created/joined game. */
    private fun consumeDiceTrials(appliedSkinId: String?, appliedHatId: String?) {
        if (appliedSkinId != null && DiceTrial.consumeSkin() != null) {
            Analytics.diceTrialUsed(appliedSkinId)
        }
        if (appliedHatId != null && DiceTrial.consumeHat() != null) {
            Analytics.diceTrialUsed(appliedHatId)
        }
    }

    /** Retires the one-time practice offer once the player has been in (or declined) a game. */
    private fun markFirstGameExperienced() {
        playerPreferences.setHasCompletedFirstGame(true)
        practiceOfferVisibleFlow.value = false
    }

    private fun startPublicGamesBrowser() {
        if (isOfflineModeFlow.value) return

        Analytics.publicGamesOpened()
        publicGameBrowserVisibleFlow.value = true
        publicGamesErrorFlow.value = null
        publicJoinErrorFlow.value = null
        publicGamesLoadingFlow.value = true
        publicGamesResultLogged = false
        publicGamesJob?.cancel()
        publicGamesJob = scope.launch {
            gameRepository.publicGamesFlow()
                .catch {
                    publicGamesErrorFlow.value = "Couldn't load public games. Check your connection and try again."
                    publicGamesLoadingFlow.value = false
                }
                .collect { games ->
                    publicGamesFlow.value = games
                    publicGamesLoadingFlow.value = false
                    publicGamesErrorFlow.value = null
                    if (!publicGamesResultLogged) {
                        publicGamesResultLogged = true
                        Analytics.publicGamesResult(games.size)
                    }
                }
        }
    }

    /**
     * Joins the game with [code] using the current player name, navigating on success. Shared by the
     * Join button and the auto-join path when an invite deep link arrives with a known player name.
     */
    private suspend fun performJoin(code: String, via: Analytics.JoinSource) {
        isLoadingFlow.value = true
        publicJoinErrorFlow.value = null
        Analytics.joinAttempted(via)
        try {
            gameRepository.setUseLocalDataSource(isOfflineModeFlow.value)
            val playerName = when (val validation = PlayerNameValidator.validate(userNameFlow.value)) {
                is PlayerNameValidator.Result.Invalid -> {
                    Analytics.joinFailed(via, "invalid_name")
                    sendJoinError(validation.reason)
                    return
                }
                is PlayerNameValidator.Result.Valid -> validation.name
            }
            if (!isOfflineModeFlow.value) {
                unlockCreatorDiceForCurrentFirebaseUser()
            }

            // A pending dice trial rides along on joins too — the trial is per-player, not
            // per-host. Spent only once the join actually succeeds.
            val trialSkinId = DiceTrial.pendingSkinId.value
            val trialHatId = DiceTrial.pendingHatId.value
            val player = Player(
                playerName,
                0,
                diceSkin = trialSkinId ?: selectedUnlockedDiceSkinId(),
                diceHat = trialHatId ?: selectedUnlockedDiceHatId(),
            )
            when (gameRepository.joinGame(player, code)) {
                JoinGameResult.JOINED -> {
                    consumeDiceTrials(trialSkinId, trialHatId)
                    userNameFlow.value = playerName
                    playerPreferences.setPlayerName(playerName)
                    markFirstGameExperienced()
                    publicGameBrowserVisibleFlow.value = false
                    publicGamesJob?.cancel()
                    publicGamesJob = null
                    joinedGameCodeFlow.value = code
                    openedGame = true
                    joinedGameIsValidFlow.send(true)
                    joinCameFromInvite = false
                    Analytics.joinedGame(offline = isOfflineModeFlow.value, via = via)
                }
                JoinGameResult.NOT_FOUND -> reportJoinFailure(via, "not_found", "Game does not exist")
                JoinGameResult.ALREADY_STARTED -> reportJoinFailure(
                    via,
                    "already_started",
                    "That game has already started.",
                )
                JoinGameResult.NAME_TAKEN -> reportJoinFailure(
                    via,
                    "name_taken",
                    "That name is already being used in this game.",
                )
                JoinGameResult.FULL -> reportJoinFailure(via, "full", "That public game is full.")
                JoinGameResult.EXPIRED -> reportJoinFailure(
                    via,
                    "expired",
                    "That public game isn't available anymore.",
                )
                JoinGameResult.VERSION_TOO_OLD -> reportJoinFailure(
                    via,
                    "version_too_old",
                    "That game needs a newer version of Banko. Update the app to join.",
                )
            }
        } catch (e: CancellationException) {
            throw e
        } catch (_: Exception) {
            reportJoinFailure(
                via,
                "backend_or_network",
                "Couldn't join game. Check your connection and try again.",
            )
        } finally {
            isLoadingFlow.value = false
        }
    }

    /**
     * Routes a join failure to where the user can actually see it. The public game browser is a
     * modal sheet that covers the root snackbar host, so while it's open the message renders inline
     * in the sheet; otherwise it goes to the snackbar as before.
     */
    private fun sendJoinError(message: String) {
        if (publicGameBrowserVisibleFlow.value) {
            publicJoinErrorFlow.value = message
        } else {
            errorMessageFlow.send(message)
        }
    }

    private fun reportJoinFailure(via: Analytics.JoinSource, reason: String, message: String) {
        Analytics.joinFailed(via, reason)
        sendJoinError(message)
    }


    private suspend fun generateValidGameCode(): String {
        var gameCode = generate5DigitAlphaNumericCode()
        // LOCAL is excluded because the repository always routes that code to the local
        // data source (see GameRepository.LOCAL_GAME_CODE).
        while (gameCode == GameRepository.LOCAL_GAME_CODE || gameRepository.gameExists(gameCode)) {
            gameCode = generate5DigitAlphaNumericCode()
        }
        return gameCode
    }

    private fun generate5DigitAlphaNumericCode(): String {
        val allowedChars = ('A'..'Z') + ('0'..'9')
        return (1..5)
            .map { allowedChars.random() }
            .joinToString("")
    }

    private fun selectedUnlockedDiceSkinId(): String {
        val purchaseState = RemoveAdsPurchases.uiState.value
        val luckyUnlocked = Achievements.diceUnlocked.value
        val creatorUnlocked = Achievements.creatorDiceUnlocked.value
        val practiceUnlocked = Achievements.practiceDiceUnlocked.value
        val selected = DiceSkin.fromId(playerPreferences.getSelectedDiceSkin())
        val activeLimitedTimeSettingIds = LimitedTimeGameModes.activeSettingIdsNow()
        val earnedLimitedTimeSkinIds = LimitedTimeDiceEntitlements.earnedSkinIds.value
        return selected
            .takeIf {
                it.isUnlocked(
                    adsRemoved = purchaseState.adsRemoved,
                    bankoPlusActive = purchaseState.bankoPlusActive,
                    luckyUnlocked = luckyUnlocked,
                    creatorUnlocked = creatorUnlocked,
                    practiceUnlocked = practiceUnlocked,
                    activeLimitedTimeSettingIds = activeLimitedTimeSettingIds,
                    earnedLimitedTimeSkinIds = earnedLimitedTimeSkinIds,
                )
            }
            ?.id
            ?: DiceSkin.DEFAULT.id
    }

    private fun selectedUnlockedDiceHatId(): String {
        val purchaseState = RemoveAdsPurchases.uiState.value
        val luckyUnlocked = Achievements.diceUnlocked.value
        val creatorUnlocked = Achievements.creatorDiceUnlocked.value
        val selected = DiceHat.fromId(playerPreferences.getSelectedDiceHat())
        return selected
            .takeIf {
                it.isUnlocked(
                    adsRemoved = purchaseState.adsRemoved,
                    bankoPlusActive = purchaseState.bankoPlusActive,
                    luckyUnlocked = luckyUnlocked,
                    creatorUnlocked = creatorUnlocked,
                )
            }
            ?.id
            ?: DiceHat.NONE.id
    }
}

internal fun hostPerksActiveForCreatedGame(purchaseState: RemoveAdsPurchaseUiState): Boolean =
    purchaseState.entitlementsResolved && purchaseState.bankoPlusActive

// The practice round's fixed roster: the player plus three bots, named to match the
// "CPU n" convention the lobby's Add Bot action uses.
private val PRACTICE_BOT_NAMES = listOf("CPU 1", "CPU 2", "CPU 3")
private const val PRACTICE_FALLBACK_NAME = "You"
private const val PRACTICE_ROUND_COUNT = 3
